import {
  EntityLinks,
  Goal,
  ID,
  MetricEntry,
  MetricEntryMode,
  Task,
  Timestamp,
} from '@griply/types';
import { startOfDay } from 'date-fns';

import { calculateMetricEntryValue } from './calculate-metric-entry-value';
import { formatDateKey } from './format-date-key';
import { buildGoalProgressCounts } from './goals/build-goal-progress-counts';
import { getLatestMetricEntry } from './goals/get-latest-metric-entry';
import {
  SubGoalProgressState,
  sumSubGoalProgress,
} from './goals/get-sub-goal-progress';
import { goalStartEntry } from './goals/goal-start-entry';

export type ComputeGoalSubgoalProgressOptions = {
  goal: Goal;
  subGoals: Goal[];
  tasks: Task[];
  links: EntityLinks;
};

export type ComputeGoalSubgoalProgressResult = {
  entries: MetricEntry[];
  target: number;
};

type ProgressDay = {
  key: string;
  date: Timestamp;
  completedSubGoalIds: ID[];
  metricEntriesBySubGoal: Map<Goal, MetricEntry[]>;
  completedTasks: Task[];
};

const groupChangesByDay = (subGoals: Goal[], tasks: Task[]): ProgressDay[] => {
  const days = new Map<string, ProgressDay>();

  const dayOf = (date: Timestamp): ProgressDay => {
    const key = formatDateKey(date);
    const day = days.get(key) ?? {
      key,
      date: startOfDay(date),
      completedSubGoalIds: [],
      metricEntriesBySubGoal: new Map(),
      completedTasks: [],
    };
    days.set(key, day);

    return day;
  };

  subGoals.forEach((subGoal) => {
    if (subGoal.completedAt) {
      dayOf(subGoal.completedAt).completedSubGoalIds.push(subGoal.id);
    }

    subGoal.metric?.entries?.forEach((entry) => {
      const { metricEntriesBySubGoal } = dayOf(entry.date);
      const dayMetricEntries = metricEntriesBySubGoal.get(subGoal) ?? [];
      dayMetricEntries.push(entry);
      metricEntriesBySubGoal.set(subGoal, dayMetricEntries);
    });
  });

  tasks.forEach((task) => {
    if (task.completedAt) {
      dayOf(task.completedAt).completedTasks.push(task);
    }
  });

  return [...days.values()].sort(
    (dayA, dayB) => dayA.date.getTime() - dayB.date.getTime(),
  );
};

export const computeGoalSubgoalProgress = ({
  goal,
  subGoals,
  tasks,
  links,
}: ComputeGoalSubgoalProgressOptions): ComputeGoalSubgoalProgressResult => {
  const subtree = [goal, ...subGoals];
  const totalCounts = buildGoalProgressCounts(tasks, subtree, { links });

  const completedSubGoalIds = new Set<ID>();
  const metricValues = new Map<ID, number>();
  const completedTaskCounts = new Map<ID, number>();

  const getState = ({ id }: Goal): SubGoalProgressState => ({
    isCompleted: completedSubGoalIds.has(id),
    taskCount: {
      completed: completedTaskCounts.get(id) ?? 0,
      total: totalCounts.get(id)?.total ?? 0,
    },
    metricValue: metricValues.get(id),
  });

  const dayEntries = groupChangesByDay(subGoals, tasks).map<MetricEntry>(
    (day) => {
      day.completedSubGoalIds.forEach((id) => completedSubGoalIds.add(id));

      day.metricEntriesBySubGoal.forEach((dayMetricEntries, subGoal) => {
        const latestEntry = getLatestMetricEntry(dayMetricEntries);
        if (!latestEntry) {
          return;
        }

        metricValues.set(
          subGoal.id,
          calculateMetricEntryValue(
            latestEntry.id,
            subGoal.metric?.entries ?? [],
            subGoal.metric?.startValue ?? 0,
          ),
        );
      });

      // Counts roll up linearly, so a day's own counts are that day's increments.
      buildGoalProgressCounts(day.completedTasks, subtree, { links }).forEach(
        ({ completed }, id) =>
          completedTaskCounts.set(
            id,
            (completedTaskCounts.get(id) ?? 0) + completed,
          ),
      );

      return {
        id: day.key,
        date: day.date,
        value: sumSubGoalProgress(subGoals, getState),
        mode: MetricEntryMode.Absolute,
      };
    },
  );

  return {
    entries: [goalStartEntry(goal), ...dayEntries],
    target: subGoals.length,
  };
};
